import is from '@sindresorhus/is';
import { z } from 'zod';

import { chatMessageIdSchema } from '~/shared/data-contract/chat/chat-ids';
import {
    chatAttachmentRefSchema,
    createJsaEntitiesMessageContentSchema,
    regenerateMessageContentSchema,
} from '~/shared/data-contract/chat/chat-user-message';

import { contextRefreshEnvelopeSchema } from './context-refresh-envelope';
import { structuredResponseEventSchema } from './structured-event';

export const assistantTextEntrySchema = z.object({
    text: z.string(),
    type: z.literal('text'),
});

export type AssistantTextEntry = z.infer<typeof assistantTextEntrySchema>;

export const assistantEventEntrySchema = z.object({
    envelope: structuredResponseEventSchema,
    type: z.literal('event'),
});

export type AssistantEventEntry = z.infer<typeof assistantEventEntrySchema>;

export const assistantContextRefreshEntrySchema = z.object({
    envelope: contextRefreshEnvelopeSchema,
    type: z.literal('context-refresh'),
});

export type AssistantContextRefreshEntry = z.infer<typeof assistantContextRefreshEntrySchema>;

export const assistantMessageEntrySchema = z.discriminatedUnion('type', [
    assistantTextEntrySchema,
    assistantEventEntrySchema,
    assistantContextRefreshEntrySchema,
]);

export type AssistantMessageEntry = z.infer<typeof assistantMessageEntrySchema>;

export const assistantMessageStatusSchema = z.enum(['complete', 'error', 'pending']);

export type AssistantMessageStatus = z.infer<typeof assistantMessageStatusSchema>;

export const agentChatUserTextMessageSchema = z.object({
    attachmentIds: z.array(z.string()).optional(),
    attachments: z.array(chatAttachmentRefSchema).optional(),
    createdAt: z.string(),
    id: chatMessageIdSchema,
    messageType: z.literal('text'),
    role: z.literal('user'),
    text: z.string(),
});

export type AgentChatUserTextMessage = z.infer<typeof agentChatUserTextMessageSchema>;

export const agentChatUserRegenerateMessageSchema = z.object({
    createdAt: z.string(),
    id: chatMessageIdSchema,
    messageType: z.literal('regenerate'),
    regenerate: regenerateMessageContentSchema,
    role: z.literal('user'),
});

export type AgentChatUserRegenerateMessage = z.infer<typeof agentChatUserRegenerateMessageSchema>;

export const agentChatUserCreateJsaEntitiesMessageSchema = z.object({
    createdAt: z.string(),
    createJsaEntities: createJsaEntitiesMessageContentSchema,
    id: chatMessageIdSchema,
    messageType: z.literal('create-jsa-entities'),
    role: z.literal('user'),
});

export type AgentChatUserCreateJsaEntitiesMessage = z.infer<typeof agentChatUserCreateJsaEntitiesMessageSchema>;

export const agentChatAssistantMessageSchema = z.object({
    createdAt: z.string(),
    entries: z.array(assistantMessageEntrySchema),
    id: chatMessageIdSchema,
    role: z.literal('assistant'),
    status: assistantMessageStatusSchema,
});

export type AgentChatAssistantMessage = z.infer<typeof agentChatAssistantMessageSchema>;

export type DroppedChatHistoryItem = {
    readonly id?: string;
    readonly issues: readonly { readonly code: string; readonly path: string }[];
    readonly kind: 'entry' | 'message';
};

export type ChatHistoryDropHandler = (item: DroppedChatHistoryItem) => void;

const agentChatMessageUnionSchema = z.union([
    agentChatUserTextMessageSchema,
    agentChatUserRegenerateMessageSchema,
    agentChatUserCreateJsaEntitiesMessageSchema,
    agentChatAssistantMessageSchema,
]);

const readId = (input: unknown): string | undefined =>
    is.plainObject(input) && is.string(input['id']) ? input['id'] : undefined;

const summarizeIssues = (error: z.ZodError): DroppedChatHistoryItem['issues'] =>
    error.issues.slice(0, 10).map((issue) => ({ code: issue.code, path: issue.path.join('.') }));

const keepParseableEntries = (entries: readonly unknown[], messageId: unknown, onDrop: ChatHistoryDropHandler) =>
    entries.filter((entry) => {
        const result = assistantMessageEntrySchema.safeParse(entry);
        if (!result.success) {
            onDrop({
                id: is.string(messageId) ? messageId : undefined,
                issues: summarizeIssues(result.error),
                kind: 'entry',
            });
        }
        return result.success;
    });

const normalizeLegacyAgentChatMessage = (input: unknown, onDrop: ChatHistoryDropHandler): unknown => {
    if (!is.plainObject(input)) return input;

    if (input['role'] === 'assistant') {
        return {
            createdAt: input['createdAt'],
            entries: is.array(input['entries']) ? keepParseableEntries(input['entries'], input['id'], onDrop) : [],
            id: input['id'],
            role: 'assistant',
            status: input['status'] ?? 'complete',
        };
    }

    if (input['role'] !== 'user') return input;

    if (input['messageType'] === 'create-jsa-entities') {
        return {
            createdAt: input['createdAt'],
            createJsaEntities: input['createJsaEntities'] ?? input['content'],
            id: input['id'],
            messageType: 'create-jsa-entities',
            role: 'user',
        };
    }

    if (input['messageType'] === 'regenerate') {
        return {
            createdAt: input['createdAt'],
            id: input['id'],
            messageType: 'regenerate',
            regenerate: input['regenerate'] ?? input['content'],
            role: 'user',
        };
    }

    return {
        attachmentIds: input['attachmentIds'],
        attachments: input['attachments'],
        createdAt: input['createdAt'],
        id: input['id'],
        messageType: 'text',
        role: 'user',
        text: input['text'] ?? '',
    };
};

const noopDropHandler: ChatHistoryDropHandler = () => undefined;

export const createAgentChatMessageSchema = (onDrop: ChatHistoryDropHandler = noopDropHandler) =>
    z.preprocess((input) => normalizeLegacyAgentChatMessage(input, onDrop), agentChatMessageUnionSchema);

export const agentChatMessageSchema = createAgentChatMessageSchema();

export type AgentChatMessage = z.infer<typeof agentChatMessageUnionSchema>;

/** Drops messages (and entries inside them) that no longer parse so one stale item cannot fail the whole history page. */
export const createAgentChatMessagePageSchema = (onDrop: ChatHistoryDropHandler = noopDropHandler) => {
    const messageSchema = createAgentChatMessageSchema(onDrop);
    return z.object({
        messages: z.array(z.unknown()).transform((items) =>
            items.flatMap((item) => {
                const result = messageSchema.safeParse(item);
                if (result.success) return [result.data];
                onDrop({ id: readId(item), issues: summarizeIssues(result.error), kind: 'message' });
                return [];
            }),
        ),
        nextCursor: z.uuid().nullable(),
    });
};

export const agentChatMessagePageSchema = createAgentChatMessagePageSchema();

export type AgentChatMessagePage = z.infer<typeof agentChatMessagePageSchema>;
